import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Element } from "react-scroll";
import {
    getHeaderCustomImagesLabel,
    getHeaderCustomText,
    getHeaderCustomTextFontSize,
    getHeaderCustomTextLabel,
    getHeaderLegalStatementLabel,
    getNewsletterDisplayName,
    getNewsletterDisplayNameStyling,
    getNewsletterHeaderImage,
    getNewsletterUseCustomFormat,
    getReactStyleLegalStatement,
    getReactStyleSectionHeader,
    shouldIncludeHeaderCustomImages,
    shouldIncludeHeaderCustomText,
    shouldIncludeHeaderLegalStatement,
    shouldIncludeHeaderNewsletterBannerInteractive,
    shouldIncludeHeaderNewsletterNameInteractive,
} from "../../../redux/selectors/template";
import {
    getCurrentNewsletterCustomImages,
    getCurrentNewsletterLegalStatement,
} from "../../../redux/selectors/newsletters";
import Article from "./NewsletterSection";
import { SectionBreakLine } from "./BreakLines";

const defaultPosition = "top";

/**
 * Return all custom (header) sections, i.e. legal statement, custom text and custom images
 * @param props
 * @returns {*}
 * @constructor
 */
const CustomSections = props => {
    const {
        shouldDisplayBanner,
        shouldDisplayNewsletterName,
        shouldDisplayLegalStatement,
        shouldDisplayCustomText,
        shouldDisplayCustomImages,
        position,
    } = props;

    const shouldDisplayAnything =
        (shouldDisplayBanner && position === defaultPosition) ||
        (shouldDisplayNewsletterName && position === defaultPosition) ||
        shouldDisplayLegalStatement ||
        (shouldDisplayCustomText && position === defaultPosition) ||
        (shouldDisplayCustomImages && position === defaultPosition);

    if (shouldDisplayAnything) {
        const children = [];

        if (shouldDisplayBanner && position === defaultPosition) {
            children.push(<HeaderImage props={props} key="headerImage" />);
        }

        if (shouldDisplayNewsletterName && position === defaultPosition) {
            children.push(
                <NewsletterName props={props} key="newsletterName" />
            );
        }

        if (shouldDisplayLegalStatement) {
            children.push(
                <LegalStatement props={props} key="legalStatement" />
            );
            children.push(<SectionBreakLine />);
        }

        if (shouldDisplayCustomText && position === defaultPosition) {
            children.push(<CustomText props={props} key="customText" />);
            children.push(<SectionBreakLine />);
        }

        if (shouldDisplayCustomImages && position === defaultPosition) {
            children.push(<CustomImages props={props} key="customImages" />);
            children.push(<SectionBreakLine />);
        }

        return (
            <div className="newsletter-content-area">
                {children.map((child, index) => (
                    <div key={index}>{child}</div>
                ))}
            </div>
        );
    } else {
        return null;
    }
};

CustomSections.propTypes = {
    shouldDisplayBanner: PropTypes.bool,
    shouldDisplayNewsletterName: PropTypes.bool,
    shouldDisplayLegalStatement: PropTypes.bool,
    shouldDisplayCustomText: PropTypes.bool,
    shouldDisplayCustomImages: PropTypes.bool,
    position: PropTypes.oneOf(["top", "bottom"]).isRequired,
};

/**
 * The HeaderImage (banner)
 * @param props
 * @returns {*}
 * @constructor
 */
const HeaderImage = ({ props }) => {
    const { headerImage } = props;
    if (headerImage) {
        return (
            <div className="newsletter-section text-center">
                <div>
                    <img
                        className="img-fluid custom-image w-100"
                        src={headerImage}
                    ></img>
                </div>
            </div>
        );
    } else {
        return null;
    }
};

/**
 * The Newsletter Name
 * @param props
 * @returns {*}
 * @constructor
 */
const NewsletterName = ({ props }) => {
    const { newsletterName, newsletterNameStyling } = props;
    return (
        <div className="newsletter-section text-center">
            <div className="section-title">
                <div style={newsletterNameStyling}>{newsletterName}</div>
            </div>
        </div>
    );
};

/**
 * The Legal Statement section
 * @param props
 * @returns {*}
 * @constructor
 */
const LegalStatement = ({ props }) => {
    const { customNewsletterFormat } = props;
    const style = {};
    let extraClass = "";

    if (!customNewsletterFormat) {
        extraClass = "force-font-size-base";
    }

    return (
        <Element name="newsletterLegalStatement">
            <div className="newsletter-section">
                {props.legalStatementLabel && (
                    <div
                        className="section-title"
                        style={props.sectionTitleStyleObject}
                    >
                        {props.legalStatementLabel}
                    </div>
                )}
                <div
                    className={`section-articles ${extraClass}`}
                    style={props.legalStatementStyleObject}
                    dangerouslySetInnerHTML={{ __html: props.legalStatement }}
                />
            </div>
        </Element>
    );
};

/**
 * The Custom Text section
 * @param props
 * @returns {*}
 * @constructor
 */
const CustomText = ({ props }) => {
    const { customTextFontSize, customNewsletterFormat } = props;
    const style = {};
    let extraClass = "";

    if (customNewsletterFormat) {
        // style.fontSize = customTextFontSize; // Disable for now because it interacts strangely with wysiwyg html
    } else {
        extraClass = "force-font-size-base";
    }

    return (
        <Element name="newsletterCustomText">
            <div className="newsletter-section">
                {props.customTextLabel && (
                    <div
                        className="section-title"
                        style={props.sectionTitleStyleObject}
                    >
                        {props.customTextLabel}
                    </div>
                )}
                <div
                    className={`section-articles ${extraClass}`}
                    dangerouslySetInnerHTML={{ __html: props.customText }}
                    style={style}
                />
            </div>
        </Element>
    );
};

/**
 * The Custom Images section
 * @param props
 * @returns {*}
 * @constructor
 */
const CustomImages = ({ props }) => {
    return (
        <Element name="newsletterCustomImages">
            <div className="newsletter-section">
                {props.customImagesLabel && (
                    <div
                        className="section-title"
                        style={props.sectionTitleStyleObject}
                    >
                        {props.customImagesLabel}
                    </div>
                )}
                <div className="section-articles">
                    {props.customImages.map((data, key) => (
                        <div key={key}>
                            <img
                                className="img-fluid custom-image"
                                src={data.url}
                            ></img>
                        </div>
                    ))}
                </div>
            </div>
        </Element>
    );
};

/**
 * mapStateToProps returns the parts of the state that will be available as props
 * @param state
 * @returns {{navigation: *}}
 */
const mapStateToProps = (state, ownProps) => {
    return {
        customNewsletterFormat: getNewsletterUseCustomFormat(state),
        shouldDisplayCustomImages: shouldIncludeHeaderCustomImages(state),
        shouldDisplayCustomText: shouldIncludeHeaderCustomText(state),
        shouldDisplayLegalStatement: shouldIncludeHeaderLegalStatement(
            state,
            ownProps.position
        ),
        shouldDisplayNewsletterName: shouldIncludeHeaderNewsletterNameInteractive(
            state
        ),
        shouldDisplayBanner: shouldIncludeHeaderNewsletterBannerInteractive(
            state
        ),
        newsletterName: getNewsletterDisplayName(state),
        newsletterNameStyling: getNewsletterDisplayNameStyling(state),
        headerImage: getNewsletterHeaderImage(state),
        legalStatementLabel: getHeaderLegalStatementLabel(state),
        legalStatementStyleObject: getReactStyleLegalStatement(state),
        legalStatement: getCurrentNewsletterLegalStatement(state),
        customTextLabel: getHeaderCustomTextLabel(state),
        customText: getHeaderCustomText(state),
        customTextFontSize: getHeaderCustomTextFontSize(state),
        customImagesLabel: getHeaderCustomImagesLabel(state),
        customImages: getCurrentNewsletterCustomImages(state),
        sectionTitleStyleObject: getReactStyleSectionHeader(state),
    };
};

/**
 * mapDispatchToProps exposes the actions we want to call as props
 * @param state
 * @returns {{navigation: *}}
 */
const mapDispatchToProps = dispatch => {
    return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomSections);
